import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { isZhxtAppMember } from '../utils/dashboard/memberMarkers';
import { fromMapLngLat, toMapLngLat } from '../utils/dashboard/basemapCoord';
import { formatLocationCommandText } from '../utils/dashboard/locationCommandHelpers';
import { hasPermission, isAdminUser } from '../utils/permissionControl';
import {
  ensureRallyPinLayer,
  setRallyPinLngLat,
  clearRallyPin,
  rallyPinHitAt
} from '../utils/dashboard/rallyPinLayer';

const PLANT_CURSOR_CLASS = 'zhxt-plant-drop-cursor';
const PLANT_CURSOR_STYLE_ID = 'zhxt-plant-drop-cursor-style';
/** 左键静止长按种会标（原 550ms） */
const LONG_PRESS_MS = 3000;
/** 超过该像素位移视为移动，取消设点 */
const MOVE_CANCEL_PX = 2;

function ensurePlantCursorStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PLANT_CURSOR_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PLANT_CURSOR_STYLE_ID;
  style.textContent = `
.${PLANT_CURSOR_CLASS},
.${PLANT_CURSOR_CLASS} .mapboxgl-canvas-container,
.${PLANT_CURSOR_CLASS} .mapboxgl-canvas,
.${PLANT_CURSOR_CLASS} canvas {
  cursor: none !important;
}
`;
  document.head.appendChild(style);
}

function canPushRescueTarget() {
  return isAdminUser() || hasPermission('救援推送');
}

function isOnDomMarker(e) {
  const t = e?.originalEvent?.target;
  if (!t || typeof t.closest !== 'function') return false;
  return !!t.closest('.mapboxgl-marker');
}

function rallyTarget() {
  return { rally: true, name: '会标' };
}

/**
 * 指挥页发送位置：左键静止长按种会标，消息框发给 Locus + 单兵 App
 *
 * 通道约定（项 3）：
 * - 权威地图钉 / App 送达：tracker_commands（command_kind=location）
 * - Locus / 目标历史：rescue_targets（必写，供 Locus 拉取）
 * - 指挥端同坐标去重：commandPin 覆盖救援钉（mapVisibility）
 */
const LOCATION_AUTHORITY_CHANNEL = 'tracker_commands';
const LOCUS_DELIVERY_CHANNEL = 'rescue_targets';

export default function useDashboardSendLocation({
  getMap,
  getBasemapId,
  drawingToolsActiveRef,
  onOutboundSent,
  onRescueTargetSent
}) {
  const [plantTarget, setPlantTarget] = useState(null);
  const [planted, setPlanted] = useState(null);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const clickHandlerRef = useRef(null);
  const moveHandlerRef = useRef(null);
  const rallyHandlersRef = useRef(null);
  const plantTargetRef = useRef(null);
  const plantedRef = useRef(null);
  const getMapRef = useRef(getMap);
  const getBasemapIdRef = useRef(getBasemapId);
  const drawingToolsActiveRefInner = useRef(drawingToolsActiveRef);
  const onOutboundSentRef = useRef(onOutboundSent);
  const onRescueTargetSentRef = useRef(onRescueTargetSent);
  const openComposeRef = useRef(() => {});

  plantTargetRef.current = plantTarget;
  plantedRef.current = planted;
  getMapRef.current = getMap;
  getBasemapIdRef.current = getBasemapId;
  drawingToolsActiveRefInner.current = drawingToolsActiveRef;
  onOutboundSentRef.current = onOutboundSent;
  onRescueTargetSentRef.current = onRescueTargetSent;

  const setMapCursorHidden = useCallback((hidden) => {
    const map = getMapRef.current?.();
    const container = map?.getContainer?.();
    if (!container) return;
    if (hidden) {
      ensurePlantCursorStyle();
      container.classList.add(PLANT_CURSOR_CLASS);
      try {
        map.getCanvas().style.cursor = 'none';
      } catch (_e) { /* ignore */ }
    } else {
      container.classList.remove(PLANT_CURSOR_CLASS);
      try {
        map.getCanvas().style.cursor = '';
      } catch (_e) { /* ignore */ }
    }
  }, []);

  const clearRallyOnMap = useCallback(() => {
    clearRallyPin(getMapRef.current?.());
  }, []);

  const showRallyAt = useCallback((lng, lat) => {
    const map = getMapRef.current?.();
    if (!map) return;
    // 优先使用传入的 lng/lat（确认种点时的精确显示坐标），避免异步竞态读到旧的 plantedRef
    ensureRallyPinLayer(map).then((ok) => {
      if (!ok) return;
      if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) {
        // 传入坐标无效时才回退到 plantedRef（悬停预览场景）
        const live = plantedRef.current;
        if (live) setRallyPinLngLat(map, live.displayLng, live.displayLat);
        return;
      }
      setRallyPinLngLat(map, lng, lat);
    });
  }, []);

  const stopMapHandlers = useCallback(() => {
    const map = getMapRef.current?.();
    if (!map) return;
    if (clickHandlerRef.current) {
      try { map.off('click', clickHandlerRef.current); } catch (_e) { /* ignore */ }
      clickHandlerRef.current = null;
    }
    if (moveHandlerRef.current) {
      try { map.off('mousemove', moveHandlerRef.current); } catch (_e) { /* ignore */ }
      moveHandlerRef.current = null;
    }
  }, []);

  const placePreview = useCallback((displayLng, displayLat, wgsLat, wgsLng) => {
    const map = getMapRef.current?.();
    if (!map) return;
    const next = {
      lat: wgsLat,
      lng: wgsLng,
      displayLng,
      displayLat
    };
    
    // Debug logging to verify coordinates are stored correctly
    console.log('[会标预览] 存储坐标:', {
      显示坐标_lng: displayLng,
      显示坐标_lat: displayLat,
      WGS84_lng: wgsLng,
      WGS84_lat: wgsLat,
      底图基准: map?.__zhxtMapDatum,
      timestamp: new Date().toISOString()
    });
    plantedRef.current = next;
    setPlanted(next);
    showRallyAt(displayLng, displayLat);
    setMapCursorHidden(false);
  }, [showRallyAt, setMapCursorHidden]);

  const exitPlantMode = useCallback(() => {
    stopMapHandlers();
    setMapCursorHidden(false);
    plantedRef.current = null;
    setPlantTarget(null);
    setPlanted(null);
    clearRallyOnMap();
    setSending(false);
    setComposeOpen(false);
  }, [stopMapHandlers, setMapCursorHidden, clearRallyOnMap]);

  const plantAtDisplayLngLat = useCallback((displayLng, displayLat, { openCompose = false, target } = {}) => {
    const map = getMapRef.current?.();
    if (!map) return false;
    const basemapId = getBasemapIdRef.current?.();
    const wgs = fromMapLngLat(displayLng, displayLat, basemapId, map) || [displayLng, displayLat];
    const wgsLng = Number(wgs[0]);
    const wgsLat = Number(wgs[1]);
    // Debug logging to trace coordinate transformations
    console.log('[发送位置] 坐标转换调试:', {
      显示坐标_lng: displayLng,
      显示坐标_lat: displayLat,
      底图ID: basemapId,
      底图基准: map?.__zhxtMapDatum,
      WGS84_lng: wgsLng,
      WGS84_lat: wgsLat,
      timestamp: new Date().toISOString()
    });
    if (!Number.isFinite(wgsLat) || !Number.isFinite(wgsLng)) {
      message.error('坐标无效');
      return false;
    }
    if (target) setPlantTarget(target);
    else if (!plantTargetRef.current) setPlantTarget(rallyTarget());
    placePreview(displayLng, displayLat, wgsLat, wgsLng);
    if (openCompose) setComposeOpen(true);
    return true;
  }, [placePreview]);

  const openCompose = useCallback(() => {
    if (!plantedRef.current) {
      message.warning('请先在地图上放置会标');
      return;
    }
    setComposeOpen(true);
  }, []);
  openComposeRef.current = openCompose;

  const startSendLocation = useCallback((member) => {
    if (!canPushRescueTarget()) {
      message.warning('无「救援推送」权限，无法发送位置');
      return;
    }
    if (!member?.name) {
      message.warning('队员姓名无效');
      return;
    }
    const map = getMapRef.current?.();
    if (!map) {
      message.warning('地图未就绪');
      return;
    }
    exitPlantMode();
    setPlantTarget(member);
    setMapCursorHidden(true);
    ensureRallyPinLayer(map);

    const onMove = (e) => {
      if (plantedRef.current) return;
      setRallyPinLngLat(map, e.lngLat.lng, e.lngLat.lat);
    };
    const onClick = (e) => {
      if (!plantTargetRef.current) return;
      if (plantedRef.current && rallyPinHitAt(map, e.point)) {
        openComposeRef.current?.();
        return;
      }
      plantAtDisplayLngLat(e.lngLat.lng, e.lngLat.lat);
    };

    moveHandlerRef.current = onMove;
    clickHandlerRef.current = onClick;
    map.on('mousemove', onMove);
    map.on('click', onClick);
    message.info(`请在地图上点击放置会标，发给「${member.name || '队员'}」`);
  }, [exitPlantMode, plantAtDisplayLngLat, setMapCursorHidden]);

  const openComposeAtWgs = useCallback((lat, lng, extra = {}) => {
    if (!canPushRescueTarget()) {
      message.warning('无「救援推送」权限，无法发送位置');
      return;
    }
    const map = getMapRef.current?.();
    if (!map) {
      message.warning('地图未就绪');
      return;
    }
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln) || Math.abs(la) > 90 || Math.abs(ln) > 180) {
      message.warning('没有可用坐标');
      return;
    }
    stopMapHandlers();
    setMapCursorHidden(false);
    const basemapId = getBasemapIdRef.current?.();
    const display = toMapLngLat(ln, la, basemapId, map) || [ln, la];
    const member = extra.member;
    setPlantTarget(member?.name ? member : rallyTarget());
    placePreview(Number(display[0]), Number(display[1]), la, ln);
    setComposeOpen(true);
  }, [stopMapHandlers, setMapCursorHidden, placePreview]);

  const openComposeAtDisplay = useCallback((displayLng, displayLat, extra = {}) => {
    if (!canPushRescueTarget()) {
      message.warning('无「救援推送」权限，无法发送位置');
      return;
    }
    const map = getMapRef.current?.();
    if (!map) {
      message.warning('地图未就绪');
      return;
    }
    const lng = Number(displayLng);
    const lat = Number(displayLat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      message.warning('没有可用坐标');
      return;
    }
    stopMapHandlers();
    setMapCursorHidden(false);
    plantAtDisplayLngLat(lng, lat, {
      openCompose: true,
      target: extra.member?.name ? extra.member : rallyTarget()
    });
  }, [stopMapHandlers, setMapCursorHidden, plantAtDisplayLngLat]);

  const submitCompose = useCallback(async ({ note, important, toAll, selectedMembers, expires_at }) => {
    const point = plantedRef.current;
    if (!point) {
      message.warning('请先在地图上放置会标');
      return;
    }
    if (!toAll && !(selectedMembers || []).length) {
      message.warning('请勾选要发送的队员，或改选「所有人」');
      return;
    }
    if (sending) return;
    setSending(true);

    const textNote = String(note || '').trim();
    const pushData = {
      lat: point.lat,
      lng: point.lng,
      icon: '旗帜',
      message: textNote || '请前往此位置',
      map_link: '',
      level: important ? '紧急' : '普通',
      to_all: !!toAll,
      org_names: [],
      group_names: [],
      user_names: toAll ? [] : (selectedMembers || []).map((m) => m.name).filter(Boolean),
      expires_at: expires_at || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
    const displayLngLat = [point.displayLng, point.displayLat];

    try {
      const response = await apiFetch('/api/rescue/target', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pushData)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const permHint = data.requiredPermission
          ? `（需要权限「${data.requiredPermission}」）`
          : '';
        throw new Error((data.error || `推送失败（HTTP ${response.status}）`) + permHint);
      }

      stopMapHandlers();
      setMapCursorHidden(false);
      plantedRef.current = null;
      plantTargetRef.current = null;
      clearRallyOnMap();

      let commandPinOk = false;
      const pendingRescueTarget = data.target || null;

      const cmdText = formatLocationCommandText(point.lat, point.lng, textNote);
      // 仅 zhxt_app 走指令通道；勾选到同名非 App 行时仍用姓名让后端按绑定令牌解析
      const appMembers = toAll
        ? []
        : (selectedMembers || []).filter((m) => isZhxtAppMember(m));
      const designatedNames = toAll
        ? []
        : (selectedMembers || []).map((m) => m.name).filter(Boolean);
      const cmdBody = {
        text: cmdText,
        important: !!important,
        pin_on_map: true,
        command_kind: 'location',
        latitude: point.lat,
        longitude: point.lng
      };
      if (toAll) {
        cmdBody.to_all = true;
      } else if (appMembers.length) {
        cmdBody.names = appMembers.map((m) => m.name).filter(Boolean);
        cmdBody.device_unique_ids = appMembers.map((m) => m.device_unique_id).filter(Boolean);
      } else if (designatedNames.length) {
        cmdBody.names = designatedNames;
      }

      if (toAll || appMembers.length || designatedNames.length) {
        try {
          const cmdRes = await apiFetch('/api/tracker/commands', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cmdBody)
          });
          const cmdData = await cmdRes.json().catch(() => ({}));
          if (cmdRes.ok && cmdData.success) {
            commandPinOk = true;
            const names = toAll
              ? ['所有人']
              : (appMembers.length
                ? appMembers.map((m) => m.name).filter(Boolean)
                : designatedNames);
            names.forEach((name) => {
              const member = appMembers.find((m) => m.name === name);
              onOutboundSentRef.current?.({
                id: `out_loc_${Date.now()}_${name}`,
                direction: 'out',
                text: cmdText,
                important: !!important,
                pin_on_map: true,
                command_kind: 'location',
                latitude: point.lat,
                longitude: point.lng,
                name,
                device_unique_id: member?.device_unique_id,
                created_by_label: cmdData.created_by_label,
                batch_id: cmdData.batch_id,
                created_at: new Date().toISOString()
              });
            });
          } else if (cmdRes.status !== 404) {
            message.warning(cmdData.error || 'Locus 已推送，单兵指令发送失败');
          }
        } catch (_cmdErr) {
          message.warning('Locus 已推送，单兵指令发送失败');
        }
      }

      // 权威通道成功时，指挥端地图以 command-pin 为准，救援钉可抑制叠画
      if (pendingRescueTarget) {
        onRescueTargetSentRef.current?.(pendingRescueTarget, {
          displayLngLat,
          mapSuppress: commandPinOk,
          authorityChannel: commandPinOk ? LOCATION_AUTHORITY_CHANNEL : LOCUS_DELIVERY_CHANNEL
        });
      }

      message.success(toAll ? '位置已推送给所有人' : `位置已推送给 ${pushData.user_names.length} 名队员`);
      setPlantTarget(null);
      setPlanted(null);
      setSending(false);
      setComposeOpen(false);
    } catch (error) {
      message.error(error.message || '推送失败');
      setSending(false);
    }
  }, [
    sending,
    stopMapHandlers,
    setMapCursorHidden,
    clearRallyOnMap
  ]);

  const plantBlocked = useCallback(() => {
    if (drawingToolsActiveRefInner.current?.current) return true;
    if (plantTargetRef.current && !plantedRef.current) return true;
    return false;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    let boundMap = null;

    const unbindRally = (map) => {
      const h = rallyHandlersRef.current;
      if (!map || !h) return;
      try { map.off('mousedown', h.onMouseDown); } catch (_e) { /* ignore */ }
      try { map.off('mousemove', h.onMouseMove); } catch (_e) { /* ignore */ }
      try { map.off('mouseup', h.onMouseUp); } catch (_e) { /* ignore */ }
      try { map.off('touchstart', h.onTouchStart); } catch (_e) { /* ignore */ }
      try { map.off('touchmove', h.onTouchMove); } catch (_e) { /* ignore */ }
      try { map.off('touchend', h.onTouchEnd); } catch (_e) { /* ignore */ }
      try { map.off('contextmenu', h.onContext); } catch (_e) { /* ignore */ }
      try { map.off('dragstart', h.onMapMotion); } catch (_e) { /* ignore */ }
      try { map.off('zoomstart', h.onMapMotion); } catch (_e) { /* ignore */ }
      try { map.off('rotatestart', h.onMapMotion); } catch (_e) { /* ignore */ }
      try { map.off('pitchstart', h.onMapMotion); } catch (_e) { /* ignore */ }
      try { map.off('click', h.onPinClick); } catch (_e) { /* ignore */ }
      try { map.off('style.load', h.onStyle); } catch (_e) { /* ignore */ }
      rallyHandlersRef.current = null;
    };

    const bindRally = (map) => {
      unbindRally(map);
      let pressTimer = null;
      let startX = 0;
      let startY = 0;
      let startLngLat = null;
      let startPoint = null;
      let moved = false;

      const clearTimer = () => {
        if (pressTimer) {
          clearTimeout(pressTimer);
          pressTimer = null;
        }
      };

      const startPress = (e) => {
        if (!canPushRescueTarget() || plantBlocked()) return;
        if (isOnDomMarker(e)) return;
        const orig = e.originalEvent;
        const clientX = orig?.clientX ?? orig?.touches?.[0]?.clientX;
        const clientY = orig?.clientY ?? orig?.touches?.[0]?.clientY;
        startX = clientX;
        startY = clientY;
        startPoint = e.point ? { x: e.point.x, y: e.point.y } : null;
        startLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
        moved = false;
        clearTimer();
        pressTimer = setTimeout(() => {
          pressTimer = null;
          if (moved || plantBlocked()) return;
          const liveMap = getMapRef.current?.();
          const fromPixel = (startPoint && liveMap?.unproject)
            ? liveMap.unproject(startPoint)
            : startLngLat;
          if (!fromPixel) return;
          plantAtDisplayLngLat(fromPixel.lng, fromPixel.lat, { openCompose: false });
          message.info('会标已放置，点会标或点「发送位置」可下发');
        }, LONG_PRESS_MS);
      };

      const maybeCancelByMove = (clientX, clientY) => {
        if (!pressTimer) return;
        const dx = (clientX ?? startX) - startX;
        const dy = (clientY ?? startY) - startY;
        if ((dx * dx) + (dy * dy) > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
          moved = true;
          clearTimer();
        }
      };

      const onMapMotion = () => {
        if (!pressTimer) return;
        moved = true;
        clearTimer();
      };

      const onMouseDown = (e) => {
        if (e.originalEvent?.button !== 0) return;
        startPress(e);
      };
      const onMouseMove = (e) => {
        maybeCancelByMove(e.originalEvent?.clientX, e.originalEvent?.clientY);
      };
      const onMouseUp = () => clearTimer();
      const onTouchStart = (e) => {
        if (!e.originalEvent?.touches?.[0]) return;
        startPress(e);
      };
      const onTouchMove = (e) => {
        const t = e.originalEvent?.touches?.[0];
        if (!t) return;
        maybeCancelByMove(t.clientX, t.clientY);
      };
      const onTouchEnd = () => clearTimer();
      const onContext = (e) => {
        e.preventDefault();
        // 右键不再种点；已有会标时可打开发送框
        if (rallyPinHitAt(map, e.point) && plantedRef.current) {
          openComposeRef.current?.();
        }
      };
      const onPinClick = (e) => {
        if (!plantedRef.current) return;
        if (!rallyPinHitAt(map, e.point)) return;
        openComposeRef.current?.();
      };
      const onStyle = () => {
        const p = plantedRef.current;
        if (!p) return;
        ensureRallyPinLayer(map).then((ok) => {
          if (!ok || !plantedRef.current) return;
          setRallyPinLngLat(map, plantedRef.current.displayLng, plantedRef.current.displayLat);
        });
      };

      const handlers = {
        onMouseDown, onMouseMove, onMouseUp,
        onTouchStart, onTouchMove, onTouchEnd, onContext, onPinClick, onStyle, onMapMotion
      };
      rallyHandlersRef.current = handlers;
      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup', onMouseUp);
      map.on('touchstart', onTouchStart);
      map.on('touchmove', onTouchMove);
      map.on('touchend', onTouchEnd);
      map.on('contextmenu', onContext);
      map.on('click', onPinClick);
      map.on('style.load', onStyle);
      map.on('dragstart', onMapMotion);
      map.on('zoomstart', onMapMotion);
      map.on('rotatestart', onMapMotion);
      map.on('pitchstart', onMapMotion);
    };

    const tryBind = () => {
      if (cancelled) return;
      const map = getMapRef.current?.();
      if (!map || !map.isStyleLoaded?.()) {
        tries += 1;
        if (tries < 80) setTimeout(tryBind, 250);
        return;
      }
      boundMap = map;
      bindRally(map);
    };

    tryBind();
    return () => {
      cancelled = true;
      if (boundMap) unbindRally(boundMap);
    };
  }, [plantAtDisplayLngLat, plantBlocked]);

  useEffect(() => () => {
    const map = getMapRef.current?.();
    if (map) {
      if (clickHandlerRef.current) {
        try { map.off('click', clickHandlerRef.current); } catch (_e) { /* ignore */ }
      }
      if (moveHandlerRef.current) {
        try { map.off('mousemove', moveHandlerRef.current); } catch (_e) { /* ignore */ }
      }
      try {
        map.getContainer()?.classList?.remove?.(PLANT_CURSOR_CLASS);
        map.getCanvas().style.cursor = '';
      } catch (_e) { /* ignore */ }
      try { clearRallyPin(map); } catch (_e) { /* ignore */ }
    }
    clickHandlerRef.current = null;
    moveHandlerRef.current = null;
  }, []);

  return {
    plantTarget,
    planted,
    sendingLocation: sending,
    composeOpen,
    startSendLocation,
    confirmSendLocation: openCompose,
    cancelSendLocation: exitPlantMode,
    closeSendLocationCompose: () => setComposeOpen(false),
    submitSendLocationCompose: submitCompose,
    openComposeAtWgs,
    openComposeAtDisplay
  };
}
